// Native interview capture. Owns only its private mic/context.
// No transcript UI, answer submission, AI playback, or shared audio graph writes.

export function createNativeCapture({
  socket,
  getTurnId,
  canCapture,
  onError,
  onReady,
}) {
  let stopped = false;
  let stream, context, source, processor, gain;
  let turnId = null;
  let sequence = 0;
  let input = [];
  let position = 0;
  let frame = [];
  let pending = [];
  let timer;
  let startPromise = null;
  let resumePromise = null;

  const resumeContext = () => {
    if (
      stopped ||
      !context ||
      context.state === "closed" ||
      context.state === "running"
    ) {
      return Promise.resolve();
    }

    if (resumePromise) return resumePromise;

    resumePromise = Promise.resolve()
      .then(() => context.resume())
      .catch((error) => {
        if (!stopped) {
          console.warn(
            "[GEMINI_NATIVE] microphone context resume pending",
            error,
          );
        }
      })
      .finally(() => {
        resumePromise = null;
      });

    return resumePromise;
  };

  const sendFrame = (samples, id, seq) => {
    const bytes = new Uint8Array(3200);
    const view = new DataView(bytes.buffer);

    samples.forEach((v, i) =>
      view.setInt16(
        i * 2,
        Math.round(
          Math.max(-1, Math.min(1, v)) * (v < 0 ? 32768 : 32767),
        ),
        true,
      ),
    );

    const data = btoa(String.fromCharCode(...bytes));

    if (
      socket.readyState === WebSocket.OPEN &&
      socket.bufferedAmount < 32768
    ) {
      try {
        socket.send(
          JSON.stringify({
            type: "native_audio",
            turn_id: id,
            sequence: seq,
            data,
          }),
        );
        return true;
      } catch (e) {
        console.warn("[GEMINI_NATIVE] transport unavailable", e);
        onError?.(e);
        stop();
      }
    }

    // Retain numbered frames until the WebSocket drain catches up.
    return false;
  };

  const reset = () => {
    input = [];
    position = 0;
    frame = [];
    pending = [];
  };

  const flush = () => {
    if (stopped) return;

    const id = getTurnId();

    if (!canCapture() || id !== turnId) {
      reset();
      return;
    }

    while (pending.length) {
      const next = pending[0];

      if (!sendFrame(next.samples, id, next.seq)) return;

      pending.shift();
    }
  };

  const stop = () => {
    stopped = true;
    clearInterval(timer);

    if (processor) processor.onaudioprocess = null;

    [source, processor, gain].forEach((node) => {
      try {
        node?.disconnect();
      } catch (e) {}
    });

    stream?.getTracks().forEach((track) => track.stop());

    if (context && context.state !== "closed") {
      context.close().catch(() => {});
    }

    reset();
  };

  const startOnce = async () => {
    if (stopped) return;

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });

      if (stopped) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const AudioContextClass =
        window.AudioContext || window.webkitAudioContext;

      context = new AudioContextClass();

      if (context.state === "suspended") {
        await context.resume();
      }

      if (stopped) {
        stop();
        return;
      }

      source = context.createMediaStreamSource(stream);
      processor = context.createScriptProcessor(4096, 1, 1);
      gain = context.createGain();
      gain.gain.value = 0;

      processor.onaudioprocess = (event) => {
        const id = getTurnId();

        if (stopped || !canCapture() || !id) {
          reset();
          return;
        }

        if (id !== turnId) {
          turnId = id;
          sequence = 0;
          reset();
        }

        input.push(...event.inputBuffer.getChannelData(0));

        const step = context.sampleRate / 16000;

        // Continuous phase across callbacks; handles 44.1/48 kHz input.
        while (position + 1 < input.length) {
          const index = Math.floor(position);
          const fraction = position - index;

          frame.push(
            input[index] * (1 - fraction) +
              input[index + 1] * fraction,
          );

          position += step;

          if (frame.length === 1600) {
            sequence += 1;

            if (pending.length >= 50) {
              onError?.(
                new Error(
                  "Microphone connection is too slow to preserve audio",
                ),
              );
              stop();
              return;
            }

            pending.push({
              samples: frame,
              seq: sequence,
            });

            frame = [];
          }
        }

        const consumed = Math.floor(position);
        input = input.slice(consumed);
        position -= consumed;
      };

      source.connect(processor);
      processor.connect(gain);
      gain.connect(context.destination);

      timer = setInterval(() => {
        if (!stopped && canCapture()) {
          void resumeContext();
        }

        flush();
      }, 100);

      onReady?.();
    } catch (e) {
      console.warn("[GEMINI_NATIVE] capture unavailable", e);
      onError?.(e);
      stop();
    }
  };

  const start = () => {
    if (!startPromise) {
      startPromise = startOnce();
    }

    return startPromise;
  };

  // Flush captured audio before the existing final answer is submitted.
  const finish = () => {
    flush();

    if (
      frame.length &&
      turnId === getTurnId() &&
      canCapture()
    ) {
      sendFrame(
        frame.concat(new Array(1600 - frame.length).fill(0)),
        turnId,
        ++sequence,
      );
    }

    reset();
  };

  const beginTurn = () => {
    if (stopped) return;

    reset();
    sequence = 0;
    turnId = getTurnId();

    // Replay keeps the same turn_id but starts a fresh microphone epoch.
    void resumeContext();
  };

  return {
    start,
    stop,
    finish,
    beginTurn,
  };
}